import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { useSelector, useDispatch } from "react-redux";
import { UserCircle2, ArrowRightLeft, CircleAlert } from "lucide-react";
import UpgradeIcon from "@mui/icons-material/North";
import VerticalAlignBottomIcon from "@mui/icons-material/South";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import axios from "axios";
import './Homepage.css';
import { Tooltip } from "@mui/material";
const DAYS_TO_SHOW = 8;

// Map common US timezone abbreviations to IANA timezone names
const TIMEZONE_MAP = {
  "Eastern": "America/New_York",
  "EST": "America/New_York",
  "EDT": "America/New_York",
  "Central": "America/Chicago",
  "CST": "America/Chicago",
  "CDT": "America/Chicago",
  "Mountain": "America/Denver",
  "MST": "America/Denver",
  "MDT": "America/Denver",
  "Pacific": "America/Los_Angeles",
  "PST": "America/Los_Angeles",
  "PDT": "America/Los_Angeles",
  "Alaska": "America/Anchorage",
  "Hawaii": "Pacific/Honolulu",
};

const getIANATimezone = (tz) => {
  if (!tz) return "America/New_York"; // default fallback
  // If already IANA format (contains "/"), use as-is
  if (tz.includes("/")) return tz;
  return TIMEZONE_MAP[tz] || TIMEZONE_MAP[tz.trim()] || "America/New_York";
};

const labels = [
  { key: "total_net_sales", label: "Total Net Sales", suffix: "$" },
  { key: "total_net_sales_py", label: "Last Year Sales", suffix: "$" },
  { key: "trans_count", label: "Total Transactions", suffix: "" },
  { key: "total_discount_sales", label: "Total Discount", suffix: "$" },
  { key: "cashier_os_amt", label: "Cashier OS Amount", suffix: "$" },
  { key: "avg_event_win_duration", label: "SOS(W)", suffix: "" },
  { key: "avg_event_ord_duration", label: "SOS(O)", suffix: "" },
  { key: "avg_event_total_duration", label: "SOS(O + W)", suffix: "" },
  { key: "calculated_daily_hrs", label: "Allowed Hours", suffix: "" },
  { key: "total_paid_hours", label: "Actual Hours", suffix: "" },
  { key: "active_employee_count", label: "Active Employee Count", suffix: "" },
  // { key: "avg_event_ord_duration", label: "Avg Event Order Duration", suffix: "" },

  { key: "total_std_cost", label: "Theoretical Food Cost", suffix: "" },
  { key: "total_actual_count_amount", label: "Actual Food costs ", suffix: "" },
  { key: "total_waste_count_amount", label: " Wastage Count", suffix: "" },

];

const keyByLabel = Object.fromEntries(labels.map((l) => [l.label, { key: l.key, suffix: l.suffix }]));

const Table = () => {
  const [tabledata, setTabledata] = useState(null);
  const [comments, setComments] = useState([]);
  const [apiCommentData, setApiCommentData] = useState(null);
  const dispatch = useDispatch();
  const formfilled = useSelector((state) => state.form.formfilled);
  const [formattedData, setFormattedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commentsInitialized, setCommentsInitialized] = useState(false);
  const [reportingTo, setReportingTo] = useState("");
  const [managerName, setManagerName] = useState("");
  const [managerPosition, setManagerPosition] = useState("");
  const [syncStatus, setSyncStatus] = useState([]);
  const [storeTimezone, setStoreTimezone] = useState(
    sessionStorage.getItem("store_timezone") || "America/New_York"
  );

  const restaurantId = sessionStorage?.getItem("store_id") || "Null";
  const email = sessionStorage.getItem("email");

  const getPastDatesAndDays = (days, timezone) => {
    const daysArray = [];
    const datesArray = [];
    const isoDatesArray = [];
    const tz = timezone || "America/New_York";

    // Get "yesterday" in the store's timezone (today's data isn't complete yet)
    const nowInTZ = new Date().toLocaleDateString("en-CA", { timeZone: tz }); // en-CA gives YYYY-MM-DD
    const todayDate = new Date(nowInTZ + "T12:00:00"); // noon to avoid date shift
    const yesterday = new Date(todayDate);
    yesterday.setDate(todayDate.getDate() - 1);

    for (let i = 0; i < days; i++) {
      const date = new Date(yesterday);
      date.setDate(yesterday.getDate() - (days - i - 1));

      const day = date.toLocaleDateString("en-US", { weekday: "short" });

      const formattedDate = date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
        weekday: "short",
      });

      // ISO string for mapping
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day_num = String(date.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day_num}`;

      datesArray.push(formattedDate);
      daysArray.push(day);
      isoDatesArray.push(isoDate);
    }

    return { daysArray, datesArray, isoDatesArray };
  };

  const { daysArray, datesArray, isoDatesArray } = getPastDatesAndDays(DAYS_TO_SHOW, storeTimezone);
  const updatedHeadings = ["Particulars", ...datesArray, "Comments"];

  const fetchData = async () => {
    try {
      setLoading(true);

      console.log("Session storage in Table Component:");

      const resp = await fetch("/server/login-function/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await resp.json();
      const storedata = data.store;
      console.log("Fetched store data:", storedata);
      sessionStorage.setItem("store_id", storedata.store_id);
      // Save store timezone for consistent date calculations
      const tz = getIANATimezone(storedata.TIMEZONE);
      sessionStorage.setItem("store_timezone", tz);
      setStoreTimezone(tz);

      setReportingTo(storedata.FranAreaDscr || "");

      // Fetch manager based on restaurant
      const managerResp = await fetch(
        `/server/mitra-integration/api/get-manager?restaurant=${storedata.store_id}`
      );
      const managerData = await managerResp.json();
      console.log("Fetched manager data:", managerData);
      
      if (managerData.manager) {
        setManagerName(managerData.manager.name || "N/A");
        setManagerPosition(managerData.manager.position || "");
      } else {
        setManagerName("N/A");
        setManagerPosition("");
      }

      // Trigger event to notify Header component
      window.dispatchEvent(new Event('sessionStorageUpdate'));

      const response = await fetch(
        `/server/mitra-integration/api/get-sales-data?restaurant=${storedata.store_id}&timezone=${encodeURIComponent(tz)}`
      );

      const commentRes = await axios.get("/server/mitra-integration/api/comments", {
        params: { store_id: storedata.store_id, timezone: tz },
      });

      const result = await response.json();

      setTabledata(result.data || []);


      const fetchedCommentData = commentRes?.data?.data || null;
      setApiCommentData(fetchedCommentData);

      // Fetch manager data
      try {
        const managerRes = await axios.get("/server/mitra-integration/api/get-manager", {
          params: { restaurant: storedata.store_id },
        });
        
        if (managerRes?.data?.manager) {
          setManagerName(managerRes.data.manager.name || "N/A");
          setManagerPosition(managerRes.data.manager.position || "");
        } else {
          setManagerName("N/A");
          setManagerPosition("");
        }
      } catch (managerError) {
        console.warn("Failed to fetch manager data:", managerError);
        setManagerName("N/A");
        setManagerPosition("");
      }

      // Fetch sync status
      try {
        const syncRes = await fetch("/server/mitra-integration/api/get-sync-status");
        const syncData = await syncRes.json();
        setSyncStatus(syncData.data || []);
      } catch (syncErr) {
        console.warn("Failed to fetch sync status:", syncErr);
      }

      setLoading(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!tabledata) return;

    // Map business_date to entry for fast lookup (normalize to YYYY-MM-DD)
    const entryByDate = {};
    (tabledata || []).forEach(entry => {
      if (entry.business_date) {
        const dateKey = entry.business_date.length > 10 ? entry.business_date.split('T')[0] : entry.business_date;
        entryByDate[dateKey] = entry;
      }
    });

    const activeLabels = [...labels];
    const hasTPMH = Object.values(entryByDate).some(e => (e.tpmh !== undefined && e.tpmh !== null) || (e.TPMH !== undefined && e.TPMH !== null));
    if (hasTPMH) {
      activeLabels.push({ key: "tpmh", label: "TPMH", suffix: "" });
    }

    // For each label, build a row with values for each date in isoDatesArray
    const formatted = activeLabels.map((field) => {
      const row = [field.label];
      isoDatesArray.forEach((isoDate) => {
        const entry = entryByDate[isoDate];
        const value = entry ? (entry[field.key] ?? entry[field.key.toUpperCase()] ?? "-") : "-";
        row.push(field.suffix === "$" && value !== "-" ? `$ ${value}` : `${value}`.trim());
      });
      row.push("");
      return row;
    });

    setFormattedData(formatted);
  }, [tabledata, isoDatesArray]);


  useEffect(() => {
    if (!formattedData || formattedData.length === 0) return;
    if (commentsInitialized) return;

    if (apiCommentData) {
      const commentsArray = formattedData.map((row) => {
        switch (row[0]) {
          case "Total Net Sales":
            return apiCommentData.total_net_sales || "";
          case "Last Year Sales":
            return apiCommentData.total_net_sales_py || "";
          case "Total Transactions":
            return apiCommentData.total_transactions || "";
          case "Total Discount":
            return apiCommentData.total_discount_sales || "";
          case "Cashier OS Amount":
            return apiCommentData.cashier_os_amt || "";
          case "SOS(W)":
            return apiCommentData.SOS_W || "";
          case "SOS(O)":
            return apiCommentData.SOS_O || "";
          case "SOS(O + W)":
            return apiCommentData.SOS_O_W || "";
          case "TPMH":
            return apiCommentData.tpmh || "";
          // case "Allowed Hours":
          //   return apiCommentData.total_paid_hours || "";
          case "Actual Hours":
            return apiCommentData.Actual_Hours || "";
          case "Active Employee Count":
            return apiCommentData.active_employee_count || "";
          // case "Avg Event Order Duration":
          //   return apiCommentData.avg_event_ord_duration || "";

          case "Total Std Cost":
            return apiCommentData.total_std_cost || "";
          case "Actual Food costs ":
            return apiCommentData.Actual_Food_costs || "";
          case " Wastage Count":
            return apiCommentData.Wastage_Count || "";

          default:
            return "";
        }
      });

      setComments(commentsArray);
    } else {
      setComments(Array(formattedData.length).fill(""));
    }

    setCommentsInitialized(true);
  }, [formattedData, apiCommentData, commentsInitialized]);
  // Map entries to isoDatesArray order for correct column mapping (normalize to YYYY-MM-DD)
  const entryByDate = {};
  (tabledata || []).forEach(entry => {
    if (entry.business_date) {
      const dateKey = entry.business_date.length > 10 ? entry.business_date.split('T')[0] : entry.business_date;
      entryByDate[dateKey] = entry;
    }
  });
  const entries = isoDatesArray.map(date => entryByDate[date] || {});

  const isCommentRequired = (rowIndex) => {
    if (!formattedData || !formattedData[rowIndex]) return false;
    const rowLabel = formattedData[rowIndex][0];
    const latestEntry = entries[entries.length - 1];

    if (!latestEntry) return false;
    const disabledLabels = [
      "Allowed Hours",
      "Active Employee Count",
      "Theoretical Food Cost",
      "Actual Food costs ",
      "Last Year Sales"
    ];
    if (disabledLabels.includes(rowLabel)) return false;

    switch (rowLabel) {
      case "Cashier OS Amount":
        if (entries.length < 8) return true;
        const cashierValues = entries.slice(0, 7).map(e => parseFloat(e.cashier_os_amt) || 0);
        const cashierAvg = cashierValues.reduce((a, b) => a + b, 0) / cashierValues.length;
        const latestCashier = parseFloat(latestEntry.cashier_os_amt) || 0;
        return latestCashier > cashierAvg;
      case "Total Discount":
        return (parseFloat(latestEntry.total_discount_sales) || 0) > 100;
      case " Wastage Count":
        if (entries.length < 8) return true;
        const wasteValues = entries.slice(0, 7).map(e => parseFloat(e.total_waste_count_amount) || 0);
        const avgWaste = wasteValues.reduce((a, b) => a + b, 0) / (wasteValues.length || 1);
        const currentWaste = parseFloat(latestEntry.total_waste_count_amount) || 0;
        return currentWaste > avgWaste;
      case "Actual Hours":
        const actual = parseFloat(latestEntry.total_paid_hours) || 0;
        const allowed = parseFloat(latestEntry.calculated_daily_hrs) || 0;
        return actual > allowed;
      default:
        return true;
    }
  };

  var isAnyCommentEmpty = comments.some((comment, index) => isCommentRequired(index) && comment.trim() === "");

  useEffect(() => {
    if (isAnyCommentEmpty) {
      dispatch({ type: "SET_FORM_FILLED", payload: false });
    } else {
      dispatch({ type: "SET_FORM_FILLED", payload: true });
    }
  }, [isAnyCommentEmpty, dispatch]);

  const handleSubmit = async () => {
    if (isAnyCommentEmpty) {
      toast.error("Please fill all comments before submitting!");
      dispatch({ type: "SET_FORM_FILLED", payload: false });
    } else {
      dispatch({ type: "SET_FORM_FILLED", payload: true });

      sessionStorage.setItem("formfilled", "true");

      const jsonData = formattedData.map((row, rowIndex) => ({
        particulars: row[0],
        days: row[7],
        comment: comments[rowIndex],
        store_id: restaurantId,
        date: (() => {
          const d = new Date();
          d.setDate(d.getDate() - 1);
          return d.toLocaleDateString("en-CA", { timeZone: storeTimezone });
        })(),
      }));

      try {
        setLoading(true);

        const response = await axios.post("/server/mitra-integration/api/comments", jsonData, {
          headers: { "Content-Type": "application/json" },
        });

        if (response.status === 200) {
          toast.success("Report submitted Successfully!");
          console.log("Database row created or updated:", response.data.message);
        } else {
          throw new Error(response.data.message || "Submission failed");
        }

      } catch (error) {
        console.error("Submit failed:", error);
        toast.error("Failed to submit report!");
      } finally {
        setLoading(false);
      }
    }
  };

  const formatValue = (value, showArrow = false, previousValue = null) => {
    const cleanValue = String(value).replace("$", "").replace(",", "").trim();
    const numericValue = parseFloat(cleanValue);

    if (isNaN(numericValue)) return value;

    const absoluteValue = Math.abs(numericValue).toLocaleString();
    const colorClass = numericValue >= 0 ? "text-dark" : "text-dark";

    let arrow = null;
    if (showArrow && previousValue !== null && previousValue !== undefined && previousValue !== "-") {
      const prevClean = String(previousValue).replace("$", "").replace(",", "").trim();
      const prevNumeric = parseFloat(prevClean);

      if (!isNaN(prevNumeric)) {
        if (numericValue > prevNumeric) {
          arrow = (
            <span className="text-green-600 ml-1 text-lg font-bold">
              <UpgradeIcon fontSize="inherit" />
            </span>
          );
        } else if (numericValue < prevNumeric) {
          arrow = (
            <span className="text-red-600 ml-1 text-lg font-bold">
              <VerticalAlignBottomIcon fontSize="inherit" />
            </span>
          );
        } else {
          arrow = (
            <span className="text-gray-800 ml-1 text-lg font-bold">
              <VerticalAlignCenterIcon fontSize="inherit" />
            </span>
          );
        }
      }
    }

    return (
      <span className={`inline-flex items-center gap-1 ${colorClass}`}>
        $ {absoluteValue}
        {arrow}
      </span>
    );
  };

  const formatNonDollarValue = (value, showArrow = false, previousValue = null) => {
    const numericValue = parseFloat(String(value).replace(",", "").trim());

    if (isNaN(numericValue)) return value;

    let arrow = null;
    if (showArrow && previousValue !== null && previousValue !== undefined && previousValue !== "-") {
      const prevNumeric = parseFloat(String(previousValue).replace(",", "").trim());

      if (!isNaN(prevNumeric)) {
        if (numericValue > prevNumeric) {
          arrow = (
            <span className="text-green-600 ml-1 text-lg font-bold">
              <UpgradeIcon fontSize="inherit" />
            </span>
          );
        } else if (numericValue < prevNumeric) {
          arrow = (
            <span className="text-red-600 ml-1 text-lg font-bold">
              <VerticalAlignBottomIcon fontSize="inherit" />
            </span>
          );
        } else {
          arrow = (
            <span className="text-gray-600 ml-1 text-lg font-bold">
              <VerticalAlignCenterIcon fontSize="inherit" />
            </span>
          );
        }
      }
    }

    return (
      <span className="inline-flex items-center gap-1">
        {value}
        {arrow}
      </span>
    );
  };


  return (
    <>
      <Toaster position="top-center" richColors closeButton />
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-500 border-t-transparent"></div>
        </div>
      )}

      <div className="flex flex-col min-h-screen p-4 bg-white">
        {/* header cards */}
        <div className="flex flex-col md:flex-row justify-around items-center gap-6 md:gap-12 p-6 bg-gradient-to-br from-white via-[#fdf5f0] to-[#f8ece5] rounded-2xl shadow-xl border border-[#fae4d0] text-center transition-all duration-300">
          <div className="flex-1 flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2 text-[#E57320]">
              <UserCircle2 className="w-6 h-6" />
              <p className="text-lg font-semibold text-gray-800">Store {managerPosition}</p>
            </div>
            <span className="bg-[#FFF5EE] text-[#E57320] px-4 py-2 border border-[#E57320] rounded-lg shadow-sm hover:bg-[#FFEFE4] transition">
              {managerName || "N/A"}
            </span>
            {/* {managerPosition && (
              <span className="text-xs text-gray-600 mt-1">{managerPosition}</span>
            )} */}
          </div>
          <div className="flex-1 flex flex-col items-center">
            <div className="flex items-center gap-2 mb-2 text-[#E57320]">
              <ArrowRightLeft className="w-6 h-6" />
              <p className="text-lg font-semibold text-gray-800">Reporting To</p>
            </div>
            <span className="bg-[#FFF5EE] text-[#E57320] px-4 py-2 border border-[#E57320] rounded-lg shadow-sm hover:bg-[#FFEFE4] transition">
              {reportingTo || "N/A"}
            </span>
          </div>
        </div>

        <div className="flex justify-center md:justify-end mt-4 mb-4">
          <button
            onClick={handleSubmit}
            disabled={loading}
            aria-busy={loading}
            className={`px-6 py-2 rounded text-white transition-colors duration-200 text-sm md:text-base ${loading ? 'bg-[#c14a1e] opacity-70 cursor-not-allowed' : 'bg-[#e57320] hover:bg-[#c14a1e]'}`}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        <div className="flex-1 rounded-[12px] shadow-lg overflow-x-auto min-h-0">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <h1 className="text-red-600 font-extrabold">
              Note *:Please Submit the table form first,and only then enable the other tabs
            </h1>
            {syncStatus.length > 0 && (() => {
              const salesSync = syncStatus.find(sync => sync.sync_type === 'Sales Data');
              const latestSync = salesSync || syncStatus.reduce((latest, sync) => {
                if (!latest || new Date(sync.last_synced_at) > new Date(latest.last_synced_at)) return sync;
                return latest;
              }, null);
              const syncDate = latestSync?.last_synced_at ? new Date(latestSync.last_synced_at) : null;
              const formatted = syncDate
                ? `${syncDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at ${syncDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                : "";
              return formatted ? (
                <span className="text-sm text-gray-500 font-semibold">
                  Last Synced: {formatted}
                </span>
              ) : null;
            })()}
          </div>
          <table className="min-w-[900px] md:min-w-full table-auto border-separate border-spacing-0 w-full">
            <thead>
              <tr>
                {updatedHeadings.map((heading, index) => (
                  <th
                    key={index}
                    className={`border border-gray-100 px-4 py-4 text-md md:text-base ${index === 8 ? "bg-[#e57320] text-white" : "bg-[#d1d1d1] text-black"
                      }`}
                  >
                    <div className="flex flex-row">
                      <span>{heading}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {formattedData?.map((row, rowIndex) => {

                // console.log("formatted data", row)

                const isTotalNetSalesRow = row[0] === "Total Net Sales";
                let shouldShowComparisonIndicatorInFirstCol = false;
                const meta = keyByLabel[row[0]];


                if (meta && entries.length > 0) {
                  const { key } = meta;
                  const pyKey = `${key}_py`;


                  if (entries[0] && entries[0][pyKey] !== undefined && entries[0][pyKey] !== null) {
                    shouldShowComparisonIndicatorInFirstCol = true;
                  }
                }

                let currentEntryIndex;
                let prevValueFromFormatted;
                let shouldShowDataCellArrow = false;
                let prevValueToCompare = null;

                return (
                  <tr key={rowIndex} className="hover:bg-[#f2f2f2] transition-colors duration-200">
                    {row.map((cell, cellIndex) => {
                      const isLastCol = cellIndex === updatedHeadings.length - 1;
                      const isParticularCol = cellIndex === 0;
                      const isEighthCol = cellIndex === 8;

                      let content;

                      const isTotalNetSalesRowForDataCell = row[0] === "Total Net Sales";

                      if (isLastCol) {

                        content = (
                          <textarea
                            className={`w-full h-16 p-2 border border-gray-300 rounded resize-none text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${["Allowed Hours", "Active Employee Count", "Theoretical Food Cost", "Last Year Sales"].includes(row[0]) ? "bg-gray-100 cursor-not-allowed" : ""
                              }`}
                            placeholder={
                              ["Allowed Hours", "Active Employee Count", "Theoretical Food Cost", "Last Year Sales"].includes(row[0])
                                ? "N/A"
                                : (isCommentRequired(rowIndex) ? "Required..." : "Optional..")
                            }
                            disabled={["Allowed Hours", "Active Employee Count", "Theoretical Food Cost", "Last Year Sales"].includes(row[0])}
                            value={comments?.[rowIndex] || ""}
                            onChange={(e) => {
                              if (!Array.isArray(comments)) {
                                console.error("Comments is not an array:", comments);
                                return;
                              }
                              const newComments = [...comments];
                              newComments[rowIndex] = e.target.value;
                              setComments(newComments);
                            }}
                          />
                        );
                      } else if (isParticularCol) {

                        content = (
                          <span className="flex items-center justify-center">
                            {cell}


                          </span>
                        );
                      } else if (cellIndex > 0 && !isLastCol && !isParticularCol) {

                        currentEntryIndex = cellIndex - 1;
                        prevValueFromFormatted = cellIndex > 1 ? row[cellIndex - 1] : null;


                        if (isTotalNetSalesRowForDataCell && meta && entries[currentEntryIndex]) {
                          const { key, suffix } = meta;
                          const currentData = entries[currentEntryIndex];

                          if (cellIndex === 1) {
                            const pyKey = `${key}_py`;
                            if (currentData[pyKey] !== undefined && currentData[pyKey] !== null) {
                              prevValueToCompare = suffix === "$" ? `$ ${currentData[pyKey]}` : `${currentData[pyKey]}`;
                              shouldShowDataCellArrow = true;
                            }
                          }
                          else {

                            const prevDayData = entries[currentEntryIndex - 1];
                            if (prevDayData && prevDayData[key] !== undefined && prevDayData[key] !== null) {
                              prevValueToCompare = prevDayData[key];
                              prevValueToCompare = suffix === "$" ? `$ ${prevValueToCompare}` : `${prevValueToCompare}`;
                              shouldShowDataCellArrow = true;
                            } else {
                              // Use prevValueFromFormatted from the outer scope
                              if (prevValueFromFormatted && prevValueFromFormatted !== "-") {
                                prevValueToCompare = prevValueFromFormatted;
                                shouldShowDataCellArrow = true;
                              }
                            }
                          }
                        }


                        if (String(cell).includes("$")) {
                          content = formatValue(cell, shouldShowDataCellArrow, prevValueToCompare)


                        } else if (cell !== "-" && !isNaN(parseFloat(String(cell).replace(",", "").trim()))) {
                          content = formatNonDollarValue(cell, shouldShowDataCellArrow, prevValueToCompare);
                        } else {
                          content = cell;
                        }
                      } else {

                        content = cell;
                      }

                      return (
                        <td
                          key={cellIndex}
                          className={`px-3 py-2 border-t border-b border-gray-300 text-sm md:text-base ${isParticularCol
                            ? "bg-[#f2f2f2] font-medium border-l border-r border-gray-300 text-center" // Class for the first column
                            : isEighthCol
                              ? "bg-[#fff5ee] border-l-[1px] border-r-[1px] border-t border-b border-gray-300 border-l-[#e57320] border-r-[#e15d26] text-right"
                              : "border-l border-r border-gray-300 text-right"
                            }`}
                        >
                          <div className="flex">


                            {content}

                            {rowIndex === 0 && cellIndex <= 8 && cellIndex !== 0 ? (() => {

                              const entryIndex = cellIndex - 1;
                              const pyValue = entries[entryIndex]?.total_net_sales_py ?? null;


                              const tooltipText = pyValue
                                ? `PY Net Sales: $ ${pyValue}`
                                : "No PY Data";

                              return (
                                <Tooltip title={tooltipText}>
                                  <span className="text-gray-500 text-center ml-1 cursor-pointer">
                                    <CircleAlert size={16} className="align-middle" />
                                  </span>
                                </Tooltip>
                              );
                            })() : null}

                          </div>


                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Table;
